<template>
  <div>
    <data-grid
      url="users"
      :columns="columns"
      :title="title"
      :actions="actions"
      :onRowClicked="onRowClicked"
      ref="grid"
      navigationPath="onRowClicked"
      navigationField="onRowClicked"
    ></data-grid>
    <v-dialog v-model="deleteUserConfirmationDialog" max-width="500">
      <v-card class="pa-2">
        <v-card-title class="headline text-danger">Are You Sure?</v-card-title>
        <v-card-text
          >This will delete User:
          <div class="font-weight-medium">
            {{ userClicked.userName }}
          </div></v-card-text
        >
        <v-card-actions>
          <div class="text-xs-right">
            <v-spacer></v-spacer>
            <v-btn
              color="default"
              dark
              @click="deleteUserConfirmationDialog = false"
              >Cancel</v-btn
            >
            <v-btn color="success" :loading="deletingUser" @click="deleteUser"
              >Delete</v-btn
            >
          </div>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="updateUserDialog" max-width="760" scrollable>
      <v-card class="user-dialog-card">
        <v-card-title class="success white--text py-2 px-4">
          <v-icon dark left>mdi-account-circle</v-icon>
          <span
            class="title font-weight-regular text-truncate mr-3"
            style="max-width: 260px; margin-bottom: 0px !important;"
            >{{ userClicked.userName }}</span
          >
          <v-spacer></v-spacer>
          <v-btn
            @click="openResetPassword()"
            v-if="!userClicked.isWindowsUser"
            class="my-0 mr-2"
            style="color: #4caf50; background-color: #FFF"
            small
          >
            <v-icon small left>mdi-lock-reset</v-icon>Reset Password
          </v-btn>
          <v-btn
            @click="openLoginHistory()"
            class="my-0"
            style="color: #4caf50; background-color: #FFF"
            small
          >
            <v-icon small left>mdi-history</v-icon>Login History
          </v-btn>
          <v-btn
            icon
            dark
            class="ma-0 ml-2 white--text"
            @click="updateUserDialog = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="px-4 py-2">

          <div class="section-label mt-2">Profile</div>
          <v-layout row wrap>
            <v-flex xs12 sm6 class="px-2">
              <v-text-field label="Name" v-model="userClicked.name" ref="name">
              </v-text-field>
            </v-flex>
            <v-flex xs12 sm6 class="px-2">
              <v-text-field
                label="Email"
                v-model="userClicked.email"
                ref="email"
                :rules="[rules.email]"
              ></v-text-field>
            </v-flex>
            <v-flex xs12 sm6 class="px-2">
              <v-text-field
                label="Phone Number"
                v-model="userClicked.phoneNumber"
                ref="phoneNumber"
                :rules="[rules.phone]"
              ></v-text-field>
            </v-flex>
            <v-flex xs12 sm6 class="px-2">
              <v-text-field
                label="UTA Phone"
                v-model="userClicked.utaPhone"
                ref="utaPhone"
                :rules="[rules.phone]"
                hint="Used to forward calls to this phone."
                persistent-hint
              ></v-text-field>
            </v-flex>
          </v-layout>

          <div class="section-label mt-2 mb-3">Access</div>
          <v-layout row wrap>
            <v-flex xs12 sm6 class="px-2">
              <v-autocomplete
                :items="branches"
                multiple
                chips
                small-chips
                deletable-chips
                label="Branch"
                placeholder="Start typing..."
                v-model="userClicked.selectedBranches"
              ></v-autocomplete>
              <v-checkbox
                v-if="!userClicked.isWindowsUser"
                class="mt-0"
                label="Disabled"
                v-model="userClicked.disabled"
                hide-details
                color="success"
              ></v-checkbox>
            </v-flex>
            <v-flex xs12 sm6 class="px-2">
              <v-autocomplete
                multiple
                label="Role"
                chips
                small-chips
                deletable-chips
                item-text="name"
                v-model="userClicked.roles"
                :items="roles"
              ></v-autocomplete>
            </v-flex>
          </v-layout>

          <div class="section-label mt-2">Attributes</div>
          <div class="attr-card mb-2">
            <v-layout class="attr-head px-3" align-center>
              <div class="attr-col-name">Name</div>
              <div class="attr-col-value">Value</div>
              <div class="attr-col-actions"></div>
            </v-layout>

            <v-divider></v-divider>

            <div class="attr-list">
              <div
                v-if="loadingAttributes"
                class="attr-empty grey--text py-3 text-xs-center"
              >
                Loading...
              </div>
              <div
                v-else-if="!userAttributes.length"
                class="attr-empty grey--text py-3 text-xs-center"
              >
                No attributes yet
              </div>
              <v-layout
                v-for="(attr, index) in userAttributes"
                :key="attr.id != null ? 'id-' + attr.id : 'idx-' + index"
                class="attr-row px-3"
                align-center
              >
                <div class="attr-col-name pr-2">
                  <v-combobox
                    v-model="attr.name"
                    :items="attributeNameOptions"
                    hide-details
                    single-line
                    :rules="[rules.required]"
                  ></v-combobox>
                </div>
                <div class="attr-col-value pr-2">
                  <v-text-field
                    v-model="attr.value"
                    hide-details
                    single-line
                  ></v-text-field>
                </div>
                <div class="attr-col-actions">
                  <v-btn
                    icon
                    small
                    flat
                    class="ma-0"
                    color="success"
                    :loading="attr.saving"
                    @click="updateUserAttribute(attr)"
                    title="Save changes"
                  >
                    <v-icon small>mdi-content-save</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    small
                    flat
                    class="ma-0"
                    color="error"
                    :loading="attr.deleting"
                    @click="deleteUserAttribute(attr)"
                    title="Delete"
                  >
                    <v-icon small>mdi-delete</v-icon>
                  </v-btn>
                </div>
              </v-layout>
            </div>

            <v-divider></v-divider>

            <v-layout class="attr-add px-3 grey lighten-4" align-center>
              <div class="attr-col-name pr-2">
                <v-combobox
                  v-model="newAttribute.name"
                  :items="attributeNameOptions"
                  hide-details
                  single-line
                  :rules="[rules.required]"
                  placeholder="Pick or type a name"
                ></v-combobox>
              </div>
              <div class="attr-col-value pr-2">
                <v-text-field
                  v-model="newAttribute.value"
                  hide-details
                  single-line
                  placeholder="Value"
                  @keyup.enter="addUserAttribute"
                ></v-text-field>
              </div>
              <div class="attr-col-actions">
                <v-btn
                  color="success"
                  small
                  class="ma-0"
                  :loading="addingAttribute"
                  @click="addUserAttribute"
                >
                  <v-icon small left>mdi-plus</v-icon>Add
                </v-btn>
              </div>
            </v-layout>
          </div>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-3">
          <v-btn
            color="error"
            outline
            :loading="deletingUser"
            @click="deleteUserConfirmationDialog = true"
          >
            <v-icon small left>mdi-delete</v-icon>Delete
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="updateUserDialog = false"
            >Cancel</v-btn
          >
          <v-btn
            :disabled="deletingUser"
            :loading="processing"
            color="success"
            @click="updateUser"
          >
            <v-icon small left>mdi-content-save</v-icon>Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="resetPasswordDialog" max-width="440" persistent>
      <v-card>
        <v-card-title class="success white--text py-2 px-4">
          <v-icon dark left>mdi-lock-reset</v-icon>
          Reset Password
        </v-card-title>
        <v-card-text class="px-4 pt-4 pb-0">
          <div class="mb-2">
            <span class="grey--text text--darken-1 mr-1">User:</span>
            <span class="font-weight-medium">{{ userClicked.userName }}</span>
          </div>
          <v-text-field
            label="New Password"
            v-model="newPassword"
            ref="newPassword"
            :type="showNewPassword ? 'text' : 'password'"
            :append-icon="showNewPassword ? 'mdi-eye-off' : 'mdi-eye'"
            @click:append="showNewPassword = !showNewPassword"
            :rules="[rules.required]"
            @keyup.enter="saveResetPassword"
          ></v-text-field>
        </v-card-text>
        <v-card-actions class="pa-3">
          <v-spacer></v-spacer>
          <v-btn
            color="default"
            dark
            :disabled="savingPassword"
            @click="resetPasswordDialog = false"
            >Cancel</v-btn
          >
          <v-btn
            color="success"
            :loading="savingPassword"
            @click="saveResetPassword"
          >
            <v-icon small left>mdi-content-save</v-icon>Save Password
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="loginHistoryDialog" max-width="850">
      <v-card class="pa-2 login-history-card">
        <v-card-title class="headline pb-1 pt-2">
          Login History - {{ userClicked.userName }}
        </v-card-title>
        <v-card-text>
          <v-progress-linear
            v-if="loadingLoginHistory"
            indeterminate
            color="success"
            class="mb-3"
          ></v-progress-linear>
          <v-layout row wrap>
            <v-flex xs12 sm4 class="pr-2">
              <v-text-field
                v-model="loginHistoryFilters.startDate"
                label="Start Date"
                type="date"
                clearable
              ></v-text-field>
            </v-flex>
            <v-flex xs12 sm4 class="pr-2">
              <v-text-field
                v-model="loginHistoryFilters.endDate"
                label="End Date"
                type="date"
                clearable
              ></v-text-field>
            </v-flex>
            <v-flex xs12 sm4>
              <v-text-field
                v-model="loginHistoryFilters.clientIP"
                label="Client IP"
                clearable
              ></v-text-field>
            </v-flex>
          </v-layout>

          <v-data-table
            :headers="loginHistoryHeaders"
            :items="filteredLoginHistory"
            :loading="loadingLoginHistory"
            :pagination.sync="loginHistoryPagination"
            :custom-sort="sortLoginHistory"
            :rows-per-page-items="[8, 25, 50, 100]"
            class="elevation-1 login-history-table"
            no-data-text="No Login History Found"
          >
            <template v-slot:items="props">
              <tr>
                <td>{{ formatDateTime(props.item.timestamp) }}</td>
                <td>{{ props.item.type }}</td>
                <td>{{ props.item.clientIP }}</td>
                <td>{{ props.item.username }}</td>
              </tr>
            </template>
          </v-data-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="loginHistoryDialog = false"
            >Close</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  data: () => ({
    title: "Users",
    columns: [
      {
        sortable: true,
        label: "User Name",
        field: "userName",
        hidden: false,
        filterOptions: {
          enabled: true,
        },
      },
      {
        sortable: true,
        label: "Name",
        field: "name",
        hidden: false,
        width: "250px",
        filterOptions: {
          enabled: true,
        },
      },
      {
        sortable: true,
        label: "Email",
        field: "email",
        hidden: false,
        filterOptions: {
          enabled: true,
        },
      },
      {
        sortable: true,
        label: "Phone",
        field: "phoneNumber",
        hidden: false,
        filterOptions: {
          enabled: true,
        },
      },
      {
        sortable: true,
        label: "Roles",
        field: "roleNames",
        width: "300px",
        hidden: false,
        filterOptions: {
          enabled: true,
        },
      },
      {
        sortable: true,
        label: "Branch",
        field: "branch",
        hidden: false,
        filterOptions: {
          enabled: true,
        },
      },
      {
        sortable: true,
        label: "Windows User",
        field: "isWindowsUser",
        width: "220px",
        type: "boolean",
        hidden: false,
        filterOptions: {
          enabled: true,
        },
      },
      {
        sortable: true,
        label: "UTA Phone",
        field: "utaPhone",
        width: "200px",
        hidden: false,
        filterOptions: {
          enabled: true,
        },
      },
      {
        sortable: true,
        label: "Disabled",
        field: "disabled",
        width: "150px",
        type: "boolean",
        hidden: false,
        filterOptions: {
          enabled: true,
        },
      },
      {
        sortable: true,
        label: "Last Login",
        field: "lastLogin",
        type: "date",
        dateOutputFormat: "MM/DD/YYYY h:mm:ss A",
        hidden: false,
        filterOptions: {
          enabled: true,
        },
      },
      {
        sortable: true,
        label: "Created By",
        field: "createdBy",
        width: "200px",
        hidden: true,
        filterOptions: {
          enabled: true,
        },
      },
      {
        sortable: true,
        label: "Created On",
        field: "createdOn",
        type: "date",
        dateOutputFormat: "MM/DD/YYYY",
        hidden: true,
        filterOptions: {
          enabled: true,
        },
      },
    ],
    actions: [
      {
        text: "Add User",
        link: "AddUser",
      },
    ],

    updateUserDialog: false,
    userClicked: {},
    processing: false,
    deletingUser: false,
    deleteUserConfirmationDialog: false,
    branches: [],

    roles: [],

    resetPasswordDialog: false,
    savingPassword: false,
    showNewPassword: false,
    resetPasswordToken: "",
    newPassword: "",

    userAttributes: [],
    knownAttributeNames: [],
    newAttribute: { name: "", value: "" },
    loadingAttributes: false,
    addingAttribute: false,

    loginHistoryDialog: false,
    loadingLoginHistory: false,
    loginHistory: [],
    loginHistoryFilters: {
      startDate: "",
      endDate: "",
      clientIP: "",
    },
    loginHistoryPagination: {
      sortBy: "timestamp",
      descending: true,
      rowsPerPage: 8,
    },
    loginHistoryHeaders: [
      {
        text: "Timestamp",
        value: "timestamp",
      },
      {
        text: "Type",
        value: "type",
      },
      {
        text: "Client IP",
        value: "clientIP",
      },
      {
        text: "Username",
        value: "username",
      },
    ],

    rules: {
      required: (value) => !!value || "Required.",
      email: (value) => {
        const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return !value || pattern.test(value) || "Invalid e-mail.";
      },
      phone: (v) => {
        const digits = (v ? v : "").replace(/\D/g, "");
        return (
          !(v ? v.trim() : v) ||
          digits.length === 10 ||
          (digits.length === 11 && digits.startsWith("1")) ||
          "Use 10 digits or 11 digits starting with 1."
        );
      },
    },
  }),
  created() {
    this.loadRoles();
    this.loadBranchList();
    this.loadAttributeNames();

    const importUsersAction = {
      text: "Import Users",
      name: "ImportUsers",
      loading: false,
    };

    importUsersAction.action = () =>
      this.importUsers(importUsersAction.loading);

    this.actions.push(importUsersAction);
  },
  computed: {
    filteredLoginHistory() {
      return this.loginHistory.filter((item) => {
        const clientIPFilter = (this.loginHistoryFilters.clientIP || "")
          .trim()
          .toLowerCase();
        const startDateFilter = (
          this.loginHistoryFilters.startDate || ""
        ).trim();
        const endDateFilter = (this.loginHistoryFilters.endDate || "").trim();

        const matchesClientIP =
          !clientIPFilter ||
          (item.clientIP || "").toLowerCase().includes(clientIPFilter);
        const itemDate = this.getDateFilterValue(item.timestamp);
        const matchesStartDate =
          !startDateFilter || itemDate >= startDateFilter;
        const matchesEndDate = !endDateFilter || itemDate <= endDateFilter;

        return matchesClientIP && matchesStartDate && matchesEndDate;
      });
    },
    attributeNameOptions() {
      const names = this.knownAttributeNames
        .concat(this.userAttributes.map((a) => a.name))
        .map((name) => (name ? name.trim() : ""))
        .filter(Boolean);

      return Array.from(new Set(names)).sort((a, b) =>
        a.toLowerCase().localeCompare(b.toLowerCase())
      );
    },
  },
  methods: {
    normalizePhone(value) {
      const digits = (value ? value : "").replace(/\D/g, "");

      if (!digits) return "";
      if (digits.length === 10) return "1" + digits;
      if (digits.length === 11 && digits.startsWith("1")) return digits;

      return "";
    },
    async loadRoles() {
      const response = await this.$http.get("odata/roles");

      this.roles = response.value.sort((a, b) => {
        if (a.name < b.name) {
          return -1;
        }
        if (a.name > b.name) {
          return 1;
        }
        return 0;
      });
    },
    async onRowClicked(user) {
      this.userClicked = JSON.parse(JSON.stringify(user));
      this.userClicked.name = this.userClicked.name
        ? this.userClicked.name
        : "";
      this.userClicked.phoneNumber = this.userClicked.phoneNumber
        ? this.userClicked.phoneNumber
        : "";
      this.userClicked.utaPhone = this.userClicked.utaPhone
        ? this.userClicked.utaPhone
        : "";

      this.userClicked.roles = await this.$http.get(
        "api/login/GetUserRoles?userName=" + user.userName
      );

      this.userClicked.selectedBranches = this.userClicked.branch
        ? this.userClicked.branch
            .split(",")
            .map((branch) => branch.trim())
            .filter(Boolean)
        : [];

      this.updateUserDialog = true;

      this.newAttribute = { name: "", value: "" };
      this.loadUserAttributes(this.userClicked.id);
    },
    async loadBranchList() {
      const branches = await this.$http.get("api/students/getBranchList?");

      branches.push("BUS");
      branches.push("ALL");
      branches.push("UTA");

      this.branches = branches.filter(Boolean).sort();
    },
    openResetPassword() {
      this.newPassword = "";
      this.showNewPassword = false;
      this.resetPasswordDialog = true;
    },
    async importUsers() {
      try {
        await this.$http.post("api/login/importAdUsers");

        this.$notify({
          group: "notification",
          text: "Users List Updated",
          type: "success",
        });

        this.$refs.grid.load();
      } catch (e) {}
    },
    formatDateTime(value) {
      if (!value) return "";

      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;

      return (
        date.toLocaleDateString() +
        " " +
        date.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        })
      );
    },
    getDateFilterValue(value) {
      if (!value) return "";

      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "";

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");

      return `${year}-${month}-${day}`;
    },
    sortLoginHistory(items, index, isDescending) {
      if (!index) return items;

      const sortBy = Array.isArray(index) ? index[0] : index;
      const descending = Array.isArray(isDescending)
        ? isDescending[0]
        : isDescending;

      return items.slice().sort((a, b) => {
        let result = 0;

        if (sortBy === "timestamp") {
          const aDate = new Date(a.timestamp);
          const bDate = new Date(b.timestamp);
          result = aDate - bDate;
        } else {
          const x = (a[sortBy] || "").toString().toLowerCase();
          const y = (b[sortBy] || "").toString().toLowerCase();
          result = x > y ? 1 : x < y ? -1 : 0;
        }

        return descending ? -result : result;
      });
    },
    async openLoginHistory() {
      if (!this.userClicked.userName) return;

      this.loginHistoryDialog = true;
      this.loadingLoginHistory = true;
      this.loginHistoryFilters.startDate = "";
      this.loginHistoryFilters.endDate = "";
      this.loginHistoryFilters.clientIP = "";

      try {
        const history = await this.$http.get(
          "api/users/GetLoginHistory?user=" + this.userClicked.userName
        );
        this.loginHistory = Array.isArray(history) ? history : [];
      } catch (e) {
        this.loginHistory = [];
      } finally {
        this.loadingLoginHistory = false;
      }
    },
    async loadAttributeNames() {
      try {
        const names = await this.$http.get("api/users/GetAttributeNames");
        this.knownAttributeNames = Array.isArray(names) ? names : [];
      } catch (e) {
        this.knownAttributeNames = [];
      }
    },
    async loadUserAttributes(userId) {
      if (!userId) {
        this.userAttributes = [];
        return;
      }

      this.loadingAttributes = true;

      try {
        const attributes = await this.$http.get(
          "api/users/GetUserAttributes?userId=" + userId
        );
        this.userAttributes = Array.isArray(attributes) ? attributes : [];
      } catch (e) {
        this.userAttributes = [];
      } finally {
        this.loadingAttributes = false;
      }
    },
    async addUserAttribute() {
      const name = this.newAttribute.name ? this.newAttribute.name.trim() : "";
      const value = this.newAttribute.value
        ? this.newAttribute.value.trim()
        : "";

      if (!name) {
        this.$notify({
          group: "notification",
          text: "Attribute name is required.",
          type: "error",
        });
        return;
      }

      this.addingAttribute = true;

      try {
        const created = await this.$http.post("api/users/AddUserAttribute", {
          userId: this.userClicked.id,
          name,
          value,
        });

        if (created) {
          this.userAttributes.push(created);
        }

        if (name && this.knownAttributeNames.indexOf(name) === -1) {
          this.knownAttributeNames.push(name);
        }

        this.newAttribute = { name: "", value: "" };

        this.$notify({
          group: "notification",
          text: "Attribute added.",
          type: "success",
        });
      } catch (e) {
      } finally {
        this.addingAttribute = false;
      }
    },
    async updateUserAttribute(attribute) {
      const name = attribute.name ? attribute.name.trim() : "";
      const value = attribute.value ? attribute.value.trim() : "";

      if (!name) {
        this.$notify({
          group: "notification",
          text: "Attribute name is required.",
          type: "error",
        });
        return;
      }

      this.$set(attribute, "saving", true);

      try {
        await this.$http.post("api/users/UpdateUserAttribute", {
          id: attribute.id,
          name,
          value,
        });

        attribute.name = name;
        attribute.value = value;

        this.$notify({
          group: "notification",
          text: "Attribute updated.",
          type: "success",
        });
      } catch (e) {
      } finally {
        this.$set(attribute, "saving", false);
      }
    },
    async deleteUserAttribute(attribute) {
      this.$set(attribute, "deleting", true);

      try {
        await this.$http.post(
          "api/users/DeleteUserAttribute?id=" + attribute.id
        );

        const index = this.userAttributes.indexOf(attribute);
        if (index !== -1) {
          this.userAttributes.splice(index, 1);
        }

        this.$notify({
          group: "notification",
          text: "Attribute removed.",
          type: "success",
        });
      } catch (e) {
        this.$set(attribute, "deleting", false);
      }
    },
    async saveResetPassword() {
      if (!this.$refs.newPassword.validate(true)) return;

      this.savingPassword = true;

      try {
        await this.$http.post("api/login/resetPassword", {
          userId: this.userClicked.id,
          //token: this.resetPasswordToken,
          newPassword: this.newPassword,
        });

        this.newPassword = "";
        this.resetPasswordDialog = false;

        this.$notify({
          group: "notification",
          text: "Password reset.",
          type: "success",
        });
      } catch (e) {
      } finally {
        this.savingPassword = false;
      }
    },
    async deleteUser() {
      this.deletingUser = true;

      try {
        await this.$http.post(
          "api/login/DeleteUser?userId=" + this.userClicked.id
        );

        this.$refs.grid.load();

        this.deleteUserConfirmationDialog = false;
        this.updateUserDialog = false;
      } catch (e) {
      } finally {
        this.deletingUser = false;
      }
    },
    async updateUser() {
      if (
        !this.$refs.email.validate(true) ||
        !this.$refs.phoneNumber.validate(true) ||
        !this.$refs.utaPhone.validate(true)
      ) {
        return;
      }

      this.processing = true;

      try {
        const name = this.userClicked.name ? this.userClicked.name.trim() : "";
        const email = this.userClicked.email
          ? this.userClicked.email.trim()
          : "";
        const phoneNumber = this.normalizePhone(this.userClicked.phoneNumber);
        const utaPhone = this.normalizePhone(this.userClicked.utaPhone);

        await this.$http.post("api/login/UpdateUser", {
          username: this.userClicked.userName,
          name,
          branch: (this.userClicked.selectedBranches || [])
            .map((branch) => (branch || "").trim())
            .filter(Boolean)
            .join(","),
          roles: this.userClicked.roles,
          email,
          phoneNumber,
          utaPhone,
          disabled: this.userClicked.disabled || false,
        });

        this.$refs.grid.load();
        this.updateUserDialog = false;
      } catch (e) {
      } finally {
        this.processing = false;
      }
    },
  },
};
</script>
<style>
.login-history-card {
  max-height: 90vh;
}

.login-history-table .v-table__overflow {
  max-height: 420px;
  overflow-y: auto;
}

.user-dialog-card {
  max-height: 90vh;
}

.section-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: #4caf50;
  margin-bottom: 2px;
  padding-left: 8px;
}

.attr-card {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
}

.attr-col-name,
.attr-col-value {
  flex: 1 1 0;
  min-width: 0;
}

.attr-col-actions {
  flex: 0 0 96px;
  text-align: right;
  white-space: nowrap;
}

.attr-head {
  min-height: 38px;
  font-size: 12px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.54);
}

.attr-list {
  max-height: 168px;
  overflow-y: auto;
}

.attr-row {
  min-height: 48px;
}

.attr-row + .attr-row {
  border-top: 1px solid #f0f0f0;
}

.attr-add {
  min-height: 56px;
}

.attr-card .v-text-field,
.attr-card .v-select {
  margin: 0;
  padding: 0;
}

.user-dialog-card .v-card__text .v-text-field,
.user-dialog-card .v-card__text .v-select {
  padding-top: 4px;
  margin-top: 0;
}

.user-dialog-card .v-card__text .attr-card .v-text-field,
.user-dialog-card .v-card__text .attr-card .v-select {
  padding-top: 0;
}
</style>
